/**
 * AdminReports - Panel de gestión de reportes de errores
 * Route: /admin/reports
 * Requiere token de autenticación
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Trash2, CheckCircle, Clock, Filter, LogOut,
  Eye, Search, Loader, RefreshCw
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function AdminReports() {
  const navigate = useNavigate();
  const { language } = useApp();
  const { isAuthenticated: authenticated, token, login, logout } = useAdminAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwordInput, setPasswordInput] = useState('');
  const [filter, setFilter] = useState('all'); // all, unresolved, resolved
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const labels = {
    es: {
      title: 'Reportes de Errores',
      subtitle: 'Gestiona los reportes de errores de los usuarios',
      login: 'Iniciar Sesión',
      password: 'Contraseña',
      logout: 'Cerrar Sesión',
      auth_title: 'Acceso Protegido',
      auth_desc: 'Ingresa la contraseña para acceder al panel de administración',
      unresolved: 'Sin Resolver',
      resolved: 'Resueltos',
      all: 'Todos',
      delete: 'Eliminar',
      mark_resolved: 'Marcar como Resuelto',
      mark_unresolved: 'Marcar como Sin Resolver',
      no_reports: 'No hay reportes',
      title_col: 'Título',
      category_col: 'Categoría',
      date_col: 'Fecha',
      status_col: 'Estado',
      actions_col: 'Acciones',
      description: 'Descripción',
      url: 'URL',
      confirm_delete: '¿Eliminar este reporte?',
      deleted: 'Reporte eliminado',
      updated: 'Reporte actualizado',
      error: 'Error al procesar',
      refresh: 'Actualizar',
      search_placeholder: 'Buscar en títulos...',
    },
    en: {
      title: 'Bug Reports',
      subtitle: 'Manage user bug reports',
      login: 'Login',
      password: 'Password',
      logout: 'Logout',
      auth_title: 'Protected Access',
      auth_desc: 'Enter the password to access the admin panel',
      unresolved: 'Unresolved',
      resolved: 'Resolved',
      all: 'All',
      delete: 'Delete',
      mark_resolved: 'Mark as Resolved',
      mark_unresolved: 'Mark as Unresolved',
      no_reports: 'No reports',
      title_col: 'Title',
      category_col: 'Category',
      date_col: 'Date',
      status_col: 'Status',
      actions_col: 'Actions',
      description: 'Description',
      url: 'URL',
      confirm_delete: 'Delete this report?',
      deleted: 'Report deleted',
      updated: 'Report updated',
      error: 'Error processing',
      refresh: 'Refresh',
      search_placeholder: 'Search titles...',
    },
  };

  const t = labels[language] || labels.en;

  const categoryLabels = {
    visual: language === 'es' ? 'Visual/UI' : 'Visual/UI',
    pokemon_data: language === 'es' ? 'Datos de Pokémon' : 'Pokémon Data',
    analytics: language === 'es' ? 'Analíticas' : 'Analytics',
    other: language === 'es' ? 'Otro' : 'Other',
  };

  // Fetch reports
  const fetchReports = async () => {
    if (!token) return;
    setRefreshing(true);
    try {
      const response = await fetch('/api/bug-reports', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          toast.error('Token expirado');
        }
        throw new Error('Failed to fetch');
      }

      const data = await response.json();
      setReports(data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load on mount
  useEffect(() => {
    if (authenticated) {
      fetchReports();
    } else {
      setLoading(false);
    }
  }, [authenticated, token]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passwordInput.trim()) {
      toast.error('Ingresa la contraseña');
      return;
    }
    try {
      await login(passwordInput);
      setPasswordInput('');
      toast.success('Acceso concedido');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Contraseña incorrecta');
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setReports([]);
  };

  // Toggle resolved status
  const toggleResolved = async (reportId, isResolved) => {
    try {
      const response = await fetch(`/api/bug-reports/${reportId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_resolved: !isResolved }),
      });

      if (!response.ok) throw new Error('Update failed');

      setReports(reports.map(r =>
        r.id === reportId ? { ...r, is_resolved: !isResolved } : r
      ));
      toast.success(t.updated);
    } catch (error) {
      console.error('Update error:', error);
      toast.error(t.error);
    }
  };

  // Delete report
  const deleteReport = async (reportId) => {
    if (!window.confirm(t.confirm_delete)) return;

    try {
      const response = await fetch(`/api/bug-reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Delete failed');

      setReports(reports.filter(r => r.id !== reportId));
      toast.success(t.deleted);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t.error);
    }
  };

  // Filter and search
  const filteredReports = reports.filter(r => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'resolved' && r.is_resolved) ||
      (filter === 'unresolved' && !r.is_resolved);

    const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <CardTitle>{t.auth_title}</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">{t.auth_desc}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t.password}
                  </label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    className={cn(
                      'w-full px-3 py-2 rounded-lg border',
                      'bg-background border-border text-foreground',
                      'focus:outline-none focus:ring-2 focus:ring-violet-500'
                    )}
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-700 hover:to-rose-600 text-white"
                >
                  {t.login}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              {t.title}
            </h1>
            <p className="text-muted-foreground mt-1">{t.subtitle}</p>
          </motion.div>

          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            {t.logout}
          </Button>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex gap-2 flex-wrap">
            {['all', 'unresolved', 'resolved'].map(f => (
              <Badge
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                className={cn(
                  'cursor-pointer px-3 py-1.5',
                  filter === f &&
                  'bg-gradient-to-r from-violet-600 to-rose-500 text-white'
                )}
                onClick={() => setFilter(f)}
              >
                {f === 'all' && t.all}
                {f === 'unresolved' && t.unresolved}
                {f === 'resolved' && t.resolved}
              </Badge>
            ))}
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.search_placeholder}
                className={cn(
                  'w-full pl-10 pr-3 py-2 rounded-lg border',
                  'bg-background border-border text-foreground',
                  'placeholder-muted-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-violet-500'
                )}
              />
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchReports()}
              disabled={refreshing}
            >
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-violet-500" />
          </div>
        ) : filteredReports.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
              <AlertTriangle className="w-8 h-8 text-muted-foreground" />
              <p className="text-muted-foreground">{t.no_reports}</p>
            </CardContent>
          </Card>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            {filteredReports.map((report, idx) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="hover:border-violet-500/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{report.title}</h3>
                          <Badge
                            variant={report.is_resolved ? 'secondary' : 'default'}
                            className={cn(
                              !report.is_resolved &&
                              'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                            )}
                          >
                            {report.is_resolved ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {t.resolved}
                              </>
                            ) : (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                {t.unresolved}
                              </>
                            )}
                          </Badge>
                        </div>

                        <p className="text-sm text-muted-foreground mb-2">
                          {categoryLabels[report.category]}
                        </p>

                        <p className="text-sm leading-relaxed mb-2">
                          {report.description}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            {new Date(report.created_at).toLocaleDateString(
                              language === 'es' ? 'es-ES' : 'en-US'
                            )}
                          </span>
                          <a
                            href={report.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 hover:text-violet-500 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            Ver URL
                          </a>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={report.is_resolved ? 'outline' : 'default'}
                          className={cn(
                            'flex-1',
                            !report.is_resolved &&
                            'bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-700 hover:to-rose-600 text-white'
                          )}
                          onClick={() => toggleResolved(report.id, report.is_resolved)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {report.is_resolved ? t.mark_unresolved : t.mark_resolved}
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                          onClick={() => deleteReport(report.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Summary */}
        {filteredReports.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 pt-6 border-t border-border flex gap-6"
          >
            <div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Sin Resolver' : 'Unresolved'}
              </p>
              <p className="text-2xl font-bold text-amber-500">
                {filteredReports.filter(r => !r.is_resolved).length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Resueltos' : 'Resolved'}
              </p>
              <p className="text-2xl font-bold text-green-500">
                {filteredReports.filter(r => r.is_resolved).length}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Total' : 'Total'}
              </p>
              <p className="text-2xl font-bold">{filteredReports.length}</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
